import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Ornament } from "@/components/Ornament";

const OPENING_DATE = new Date("2026-07-15T23:59:59");
const ILLUSTRATION = "https://static.prod-images.emergentagent.com/jobs/a0a3e924-7d4d-40ec-a0bc-feda19bde012/images/1b78f63242fd83c8816146c736879ec2c9b3db374090c472ebe38dda8e79dd30.png";

// Grand-opening pop-up: once per session, until 15 July 2026.
export const OpeningPopup = () => {
  const { t } = useLang();
  const o = t.opening;
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (new Date() > OPENING_DATE) return;
    if (sessionStorage.getItem("mara-opening-seen")) return;
    const timer = setTimeout(() => setShow(true), 1400);
    return () => clearTimeout(timer);
  }, []);

  const close = () => {
    sessionStorage.setItem("mara-opening-seen", "1");
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="opening-popup"
          className="fixed inset-0 z-[9500] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
        >
          <div className="absolute inset-0 bg-espresso-deep/80 backdrop-blur-sm" onClick={close} data-testid="opening-backdrop" />

          <motion.div
            className="relative w-full max-w-3xl bg-espresso rounded-3xl overflow-hidden shadow-[0_40px_120px_rgba(0,0,0,0.6)] ring-1 ring-gold/30 grid md:grid-cols-2"
            initial={{ scale: 0.9, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <button onClick={close} data-testid="opening-close" aria-label={o.close}
              className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-espresso-deep/70 hover:bg-gold text-cream hover:text-espresso-deep flex items-center justify-center transition-all duration-300 ring-1 ring-gold/30">
              <X className="w-5 h-5" />
            </button>

            {/* Illustration */}
            <div className="relative hidden md:block">
              <img src={ILLUSTRATION} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-espresso/40" />
            </div>

            {/* Content */}
            <div className="relative p-8 md:p-10 flex flex-col justify-center text-center md:text-left grain-overlay">
              <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 70% 20%, rgba(200,169,126,0.18), transparent 60%)" }} />
              <div className="relative">
                <span className="inline-block text-[11px] tracking-[0.3em] uppercase text-gold bg-gold/10 border border-gold/30 rounded-full px-4 py-1.5">{o.badge}</span>
                <h2 className="mt-5 font-serif text-3xl md:text-4xl text-cream leading-[1.1]">{o.title}</h2>
                <Ornament className="my-5 justify-center md:justify-start opacity-70" width={180} />
                <p className="text-cream/75 font-sans text-sm leading-relaxed">{o.body}</p>
                <Link to="/brockenstube" onClick={close} data-testid="opening-cta"
                  className="group relative mt-7 inline-flex items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-3.5 text-espresso-deep font-sans text-sm font-medium tracking-wide overflow-hidden shadow-[0_0_22px_rgba(200,169,126,0.55)] transition-all duration-300 hover:bg-gold-light hover:scale-[1.03] hover:shadow-[0_0_38px_rgba(200,169,126,0.85)] active:scale-95">
                  <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out -skew-x-12 bg-gradient-to-r from-transparent via-white/55 to-transparent" />
                  <span className="relative z-10 flex items-center gap-2.5">{o.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OpeningPopup;
